import { FC } from 'react';
import { StyleSheet } from '@react-pdf/renderer';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { PeriodEndReportFundCashFlowAndIrr } from 'further-types/lp/period-end-report';
import PdfAccountingTable from '../Pdf/components/PdfAccountingTable';
import Typography from '../Pdf/components/Typography';
import Column from '../Pdf/components/Column';
import theme from '../Pdf/components/theme';
import CalendarDay from '../../utils/calendar-day';
import { numberToCurrencyString } from '../../utils/currency';
import getFundCashFlowAndIrrSummaryItems from './helpers/get-fund-cash-flow-and-irr-summary-items';
import FundCashFlowAndIrrSummary from './components/pdf/FundCashFlowAndIrrSummary';
import LabelColumnHeader from './components/pdf/LabelColumnHeader';
import PeriodEndReportPage from './components/pdf/PeriodEndReportPage';
import SectionTitle from './components/pdf/SectionTitle';
import SubsectionTitle from './components/pdf/SubsectionTitle';
import { PeriodEndReportSectionPageProps } from './types';

const styles = StyleSheet.create({
  content: {
    paddingTop: theme.spacing(theme.spacingSizes.xs),
    gap: '12px',
  },
  heading: {
    paddingLeft: '10px',
  },
});

type Props = PeriodEndReportSectionPageProps & {
  vehicleName: string;
  baseCurrency: CurrencyCode;
  periodEnd: CalendarDay;
  fundCashFlowAndIrr: PeriodEndReportFundCashFlowAndIrr;
  hasFundValueBridge: boolean;
  hasFundValueProgression: boolean;
  hasFundPerformanceMetrics: boolean;
};

const FundCashFlowAndIrr: FC<Props> = ({
  vehicleName,
  baseCurrency,
  periodEnd,
  fundCashFlowAndIrr,
  hasFundValueBridge,
  hasFundValueProgression,
  hasFundPerformanceMetrics,
  sectionNumber,
  ...rest
}) => {
  const {
    paidInCapital,
    netDistributions,
    carryDistributions,
    residualValue,
    cashFlowsAndResidualValue,
  } = fundCashFlowAndIrr.totals;
  const displaySectionTitle =
    !hasFundValueBridge &&
    !hasFundValueProgression &&
    !hasFundPerformanceMetrics;
  const subsectionNumber =
    [
      hasFundValueBridge,
      hasFundValueProgression,
      hasFundPerformanceMetrics,
    ].filter(Boolean).length + 1;
  const columnHeaders = [
    {
      key: 'event',
      label: 'Event',
      weight: 2.1,
    },
    {
      key: 'cash-flow-date',
      label: 'Cash flow date',
      weight: 1.35,
    },
    {
      key: 'paid-in-capital',
      label: 'Paid-in capital',
      weight: 1.35,
    },
    {
      key: 'net-distributions',
      label: 'Net distributions',
      weight: 1.45,
    },
    {
      key: 'carry-distributions',
      label: 'Carry distributions',
      weight: 1.45,
    },
    {
      key: 'residual-value',
      label: 'Residual value (RV)',
      weight: 1.45,
    },
    {
      key: 'cash-flows-and-rv',
      label: 'Cash flows and RV',
      weight: 1.45,
    },
  ];

  const summaryItems = getFundCashFlowAndIrrSummaryItems(fundCashFlowAndIrr);

  const rowFontSize = 6;
  const rowLineHeight = 6;
  const rowFontWeight = 500;

  const formatTotalAmount = (value: number) =>
    numberToCurrencyString(value, {
      currency: baseCurrency,
      negativeInBrackets: true,
    });

  const formatTableAmount = (value: number) => {
    if (value === 0) {
      return (
        <Typography
          color={theme.palette.grey[500]}
          style={{
            fontSize: `${rowFontSize}px`,
            fontWeight: rowFontWeight,
            lineHeight: `${rowLineHeight}px`,
          }}
        >
          --
        </Typography>
      );
    }

    return formatTotalAmount(value);
  };

  return (
    <PeriodEndReportPage
      {...rest}
      header={`0${sectionNumber} – Fund performance summary`}
    >
      {displaySectionTitle ? (
        <SectionTitle
          sectionNumber={sectionNumber}
          title="Fund performance summary"
        />
      ) : null}
      <SubsectionTitle
        sectionNumber={sectionNumber}
        subsectionNumber={subsectionNumber}
        title="Cash flows and IRR calculation"
        paddedTop={displaySectionTitle}
      />
      <Column style={styles.content}>
        <Column style={styles.heading}>
          <LabelColumnHeader
            title={`Cash flows for ${vehicleName}`}
            subtitle={`All figures as of ${periodEnd.toReadable()}`}
          />
        </Column>
        <PdfAccountingTable
          columnHeaders={columnHeaders}
          rows={[
            ...fundCashFlowAndIrr.rows.map((row) => ({
              id: row.id,
              indentation: 12,
              paddingY: 3,
              paddingX: 10,
              fontWeight: rowFontWeight,
              fontSize: rowFontSize,
              lineHeight: rowLineHeight,
              cellLeftBorders: true,
              cells: [
                row.eventLabel,
                row.cashFlowDate.toReadable(),
                formatTableAmount(row.paidInCapital),
                formatTableAmount(row.netDistributions),
                formatTableAmount(row.carryDistributions),
                formatTableAmount(row.residualValue),
                formatTableAmount(row.cashFlowsAndResidualValue),
              ],
            })),
            {
              id: 'total',
              indentation: 12,
              paddingY: 3,
              paddingX: 10,
              fontWeight: rowFontWeight,
              fontSize: rowFontSize,
              lineHeight: rowLineHeight,
              backgroundColor: theme.palette.grey[50],
              cellLeftBorders: [2, 3, 4, 5, 6],
              cells: [
                'Total',
                '',
                formatTotalAmount(paidInCapital),
                formatTotalAmount(netDistributions),
                formatTotalAmount(carryDistributions),
                formatTotalAmount(residualValue),
                formatTotalAmount(cashFlowsAndResidualValue),
              ],
            },
          ]}
          rounded
          headerPaddingY={5}
          headerPaddingX={10}
          headerFontSize={6}
          headerLineHeight={6}
        />
        <FundCashFlowAndIrrSummary summaryItems={summaryItems} />
      </Column>
    </PeriodEndReportPage>
  );
};

export default FundCashFlowAndIrr;
